/**
 * Thank-you confirmation shown after a public feature-request submit.
 *
 * Routed at `/submit-feature-request/thanks` — the wizard navigates
 * here after a successful POST /submit, passing `?id=<featureRequestId>`.
 */
import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, ArrowRight } from "lucide-react";


export default function PublicFeatureRequestThanksPage() {
    const [params] = useSearchParams();
    const featureRequestId = params.get("id");
    const [token, setToken] = useState(null);

    // Read the access token off sessionStorage so we can show it to the
    // user one final time. After this page they should treat the token
    // as gone unless they save it.
    useEffect(() => {
        try { setToken(sessionStorage.getItem("frPublicToken")); } catch { /* sandboxed env */ }
    }, []);

    return (
        <div className="min-h-screen bg-[#050505] text-white flex flex-col"
             data-testid="public-feature-request-thanks">
            <header className="border-b border-white/10 bg-[#0a0a0a]/80">
                <div className="max-w-3xl mx-auto px-4 sm:px-8 py-5">
                    <Link to="/submit-feature-request"
                          className="text-sm text-white/55 hover:text-white transition"
                          data-testid="thanks-submit-another">
                        ← Submit another feature request
                    </Link>
                </div>
            </header>
            <main className="flex-1 max-w-3xl mx-auto w-full px-4 sm:px-8 py-12">
                <div className="border border-emerald-300/30 bg-emerald-300/[0.05] rounded-2xl p-8">
                    <div className="flex items-center gap-3 mb-4">
                        <CheckCircle2 className="w-7 h-7 text-emerald-300" strokeWidth={1.5} />
                        <h1 className="font-display text-2xl sm:text-3xl">Thanks — your request is in.</h1>
                    </div>
                    <p className="text-sm text-white/65 leading-relaxed">
                        The SiteCentric product team will review it and reach out at the email
                        you provided. You don&apos;t need to do anything further.
                    </p>
                </div>

                {featureRequestId ? (
                    <div className="mt-6 border border-white/10 rounded-xl p-5 bg-white/[0.02]"
                         data-testid="thanks-confirmation-card">
                        <div className="text-[11px] uppercase tracking-wider text-white/45 mb-1">
                            Your reference
                        </div>
                        <div className="font-mono text-xs sm:text-sm text-white/80 break-all">
                            {featureRequestId}
                        </div>
                        {token ? (
                            <>
                                <div className="text-[11px] uppercase tracking-wider text-white/45 mt-4 mb-1">
                                    Access token (one-time)
                                </div>
                                <div className="font-mono text-[11px] text-white/65 break-all" data-testid="thanks-token">
                                    {token}
                                </div>
                                <div className="text-[11px] text-white/40 mt-2 leading-relaxed">
                                    Save these if you want to revisit your submission from another browser.
                                    For privacy, we won&apos;t email them.
                                </div>
                            </>
                        ) : null}
                    </div>
                ) : null}

                <div className="mt-8 flex items-center gap-3">
                    <Link to="/submit-feature-request"
                          className="px-4 py-2 bg-[#7C3AED] hover:bg-[#6D28D9] text-white text-sm rounded-md inline-flex items-center gap-2"
                          data-testid="thanks-submit-another-btn">
                        Submit another <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>
            </main>
            <footer className="border-t border-white/10 py-6 text-center text-[11px] text-white/35">
                SiteCentric AI · CTMS Helper Console
            </footer>
        </div>
    );
}
